import React, { useState, useMemo, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { PrimaryButton } from "@fluentui/react";
import { Delete20Regular } from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";
import { usePromptTemplates } from "../../context/PromptTemplateContext";
import { PromptTemplate, PromptTemplateVersion } from "../../models/PromptTemplate";
import { type VersionStatus } from "../../api/api";
import { ConfirmDialog } from "../../shell/components/ConfirmDialog/ConfirmDialog";
import Layout from "../../pages/layout/Layout";
import styles from "./PromptTemplateManager.module.css";

// ---------------------------------------------------------------------------
// NewVersionModal — extracted from the inline IIFE to keep the parent render
// method readable and to give the modal a stable identity in React's tree.
// ---------------------------------------------------------------------------
interface NewVersionModalProps {
  tpl: PromptTemplate;
  newVersionContent: string;
  onContentChange: (content: string) => void;
  onClose: () => void;
  onSubmit: () => void;
}

const NewVersionModal: React.FC<NewVersionModalProps> = ({ tpl, newVersionContent, onContentChange, onClose, onSubmit }) => {
  // nextVersion: totalVersions includes the Default (v0) row, so the next
  // version number equals the count. E.g. [v0] → totalVersions=1 → next=v1.
  const nextVersion = tpl.totalVersions ?? 1;
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Create New Version &mdash; {tpl.name}</h2>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>
        <div className={styles.form}>
          {tpl.hasPendingDraft && (
            <div className={styles.versionNotice} style={{ borderColor: '#f5a623', backgroundColor: '#fff8ec' }}>
              <p>⚠️ <strong>Draft v{tpl.pendingDraftVersion} is already pending review.</strong> Creating another draft (v{nextVersion}) is allowed, but consider approving or reviewing the existing draft first.</p>
            </div>
          )}
          <div className={styles.versionNotice}>
            <p>This will create <strong>v{nextVersion}</strong> as a <strong>Draft</strong>.
            {(tpl.currentVersion != null && tpl.currentStatus === 'Approved')
              ? ` Current v${tpl.currentVersion} will remain active until this draft is approved.`
              : " This template has no approved version yet — users cannot see it until a draft is approved."}
            </p>
          </div>
          <div className={styles.formGroup}>
            <label>Updated Content *</label>
            <textarea
              value={newVersionContent}
              onChange={(e) => onContentChange(e.target.value)}
              rows={15}
              required
              placeholder="Paste updated prompt content here..."
            />
          </div>
          <div className={styles.formActions}>
            <button className={styles.cancelButton} onClick={onClose}>Cancel</button>
            <button className={styles.submitButton} onClick={onSubmit}>Create Version (Draft)</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const PromptTemplateManager: React.FC = () => {
  const navigate = useNavigate();
  const {
    templates,
    canManagePrompts,
    loading,
    reloadTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    getVersions,
    createVersion,
    changeVersionStatus,
  } = usePromptTemplates();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<PromptTemplate | null>(null);
  const [formData, setFormData] = useState({
    promptKey: "",
    name: "",
    description: "",
    content: "",
    category: "",
    tags: [] as string[],
  });

  // Per-template state
  const [historyTemplateId, setHistoryTemplateId] = useState<string | null>(null);
  const [versions, setVersions] = useState<PromptTemplateVersion[]>([]);
  const [newVersionTemplateId, setNewVersionTemplateId] = useState<string | null>(null);
  const [newVersionContent, setNewVersionContent] = useState("");
  const [expandedVersionId, setExpandedVersionId] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState("");
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [categorySearchInput, setCategorySearchInput] = useState("");
  const [customCategories, setCustomCategories] = useState<string[]>([]);
  const [showVersionDropdown, setShowVersionDropdown] = useState(false);

  // Edit-modal version browsing
  const [editVersions, setEditVersions] = useState<PromptTemplateVersion[]>([]);
  const [selectedEditVersionId, setSelectedEditVersionId] = useState<string | "">("");
  const [modalError, setModalError] = useState<string | null>(null);
  const [isVersionsLoading, setIsVersionsLoading] = useState(false);
  // Inline draft editing — holds the live textarea content when a Draft is selected.
  const [draftEditContent, setDraftEditContent] = useState<string | null>(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // Simple in-memory cache: templateId → versions[], invalidated on mutation.
  const versionCacheRef = useRef<Map<string, PromptTemplateVersion[]>>(new Map());

  // Refs for custom dropdown containers — used to detect focus leaving the
  // dropdown so we can close it without relying on setTimeout (race-free).
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const tagDropdownRef = useRef<HTMLDivElement>(null);
  const versionDropdownRef = useRef<HTMLDivElement>(null);

  // Inline notification banner (replaces alert() for non-modal errors/info).
  const [notification, setNotification] = useState<{ message: string; kind: "error" | "info" } | null>(null);
  const clearNotification = () => setNotification(null);

  // Delete confirmation dialog state
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Track whether the component is still mounted to guard async state updates.
  // The setup body resets to true explicitly so that React StrictMode's double-invocation
  // (setup → cleanup → setup) doesn't leave isMountedRef permanently false.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Close any open modal when the user presses Escape for accessible keyboard navigation.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isModalOpen) {
        setIsModalOpen(false);
        setModalError(null);
        resetFormAndModalState();
      }
      else if (newVersionTemplateId) setNewVersionTemplateId(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isModalOpen, newVersionTemplateId]);

  // Known categories — fixed list; does not read from existing templates to avoid
  // surfacing stale/legacy category names from the database.
  const knownCategories = useMemo(() => {
    const cats = new Set(["Product Management", "R&D", "Professional Services", "Pre-sales", "O&M"]);
    customCategories.forEach(c => cats.add(c));
    return [...cats].sort((a, b) => a.localeCompare(b));
  }, [customCategories]);

  // Filtered categories based on search input
  const filteredCategories = useMemo(() => {
    if (!categorySearchInput.trim()) return knownCategories;
    const q = categorySearchInput.toLowerCase();
    return knownCategories.filter(c => c.toLowerCase().includes(q));
  }, [knownCategories, categorySearchInput]);

  // Known tags derived from existing templates
  const knownTags = useMemo(() => {
    const tags = new Set<string>();
    templates.forEach(t => t.tags?.forEach(tag => tags.add(tag)));
    return [...tags].sort((a, b) => a.localeCompare(b));
  }, [templates]);

  /** Resets all form and modal-related state to its initial values. */
  const resetFormAndModalState = () => {
    setEditingTemplate(null);
    setEditVersions([]);
    setSelectedEditVersionId("");
    setTagInput("");
    setShowTagSuggestions(false);
    setShowCategoryDropdown(false);
    setCategorySearchInput("");
    setShowVersionDropdown(false);
    setIsVersionsLoading(false);
    setFormData({ promptKey: "", name: "", description: "", content: "", category: "", tags: [] });
  };

  /** Display label for a version number: 0 = "Default", 1+ = "v1", "v2" etc. */
  const versionLabel = (v: number) => v === 0 ? "Default" : `v${v}`;

  /** Display status for a version: version 0 always shows "Default" */
  const versionStatusLabel = (v: PromptTemplateVersion) => v.version === 0 ? "Default" : v.status;

  if (!canManagePrompts) {
    return null;
  }

  const handleOpenModal = async (template?: PromptTemplate) => {
    clearNotification();
    if (template) {
      setEditingTemplate(template);
      setFormData({
        promptKey: template.promptKey,
        name: template.name,
        description: template.description || "",
        content: template.content,
        category: template.category,
        tags: template.tags || [],
      });
      // Serve from cache if available; otherwise fetch with a loading indicator.
      const cached = versionCacheRef.current.get(template.id);
      if (cached) {
        setEditVersions(cached);
        setSelectedEditVersionId("");
      } else {
        setIsVersionsLoading(true);
        try {
          const v = await getVersions(template.id);
          if (isMountedRef.current) {
            versionCacheRef.current.set(template.id, v);
            setEditVersions(v);
            setSelectedEditVersionId("");
          }
        } catch (error) {
          console.error("Failed to load template versions:", error);
          if (isMountedRef.current) {
            setEditVersions([]);
            setSelectedEditVersionId("");
            setModalError("Could not load version history for this template. You can still edit metadata.");
          }
        } finally {
          // Always clear the loading state — React 18 ignores state updates on unmounted
          // components, so this is safe even if the component is no longer mounted.
          setIsVersionsLoading(false);
        }
      }
    } else {
      resetFormAndModalState();
    }
    setTagInput("");
    setShowTagSuggestions(false);
    setShowCategoryDropdown(false);
    setShowVersionDropdown(false);
    setModalError(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalError(null);
    resetFormAndModalState();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.category) {
      setModalError("Please select a category.");
      return;
    }
    try {
      if (editingTemplate) {
        // Send empty content for metadata-only edits — content changes go through versioning.
        await updateTemplate({ ...editingTemplate, ...formData, content: "" });
        // Invalidate version cache for edited template
        versionCacheRef.current.delete(editingTemplate.id);
      } else {
        await createTemplate(formData);
      }
      handleCloseModal();
      setNotification({ message: editingTemplate ? "Template updated successfully." : "Template created successfully.", kind: "info" });
      // Force a fresh reload to ensure the template list (and chatbox picker) reflect the latest state.
      await reloadTemplates();
    } catch (error) {
      console.error("Failed to save template:", error);
      const msg = error instanceof Error ? error.message : "Unknown error";
      setModalError(`Failed to save template: ${msg}`);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      await deleteTemplate(deleteConfirmId);
      versionCacheRef.current.delete(deleteConfirmId);
    } catch (error) {
      console.error("Failed to delete template:", error);
      setNotification({ message: "Failed to delete template. Please try again.", kind: "error" });
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const addTag = (tag: string) => {
    // Tags are intentionally normalised to lowercase for consistent storage and
    // duplicate detection. Display capitalisation is handled via CSS if needed.
    const t = tag.trim().toLowerCase();
    if (!t) return;
    // Reject tags that contain HTML-special characters to prevent injection if tags
    // are ever rendered without React's default text-node escaping (e.g. in a tooltip
    // attribute or third-party component). React auto-escapes JSX text, but this
    // guard protects future rendering paths.
    if (/[<>"'`]/.test(t)) {
      setNotification({ message: "Tags must not contain HTML special characters (< > \" ' `).", kind: "error" });
      return;
    }
    setFormData(prev => {
      const currentTags = prev.tags ?? [];
      // avoid duplicates (case-insensitive)
      const exists = currentTags.some(x => x.toLowerCase() === t);
      if (exists) return prev;
      return { ...prev, tags: [...currentTags, t] };
    });
    // hide suggestions and clear input
    setShowTagSuggestions(false);
    setTagInput("");
  };

  const removeTag = (tag: string) => {
    const t = tag.trim().toLowerCase();
    setFormData(prev => ({ ...prev, tags: (prev.tags ?? []).filter(x => x.toLowerCase() !== t) }));
  };

  const toggleHistory = async (template: PromptTemplate) => {
    if (historyTemplateId === template.id) {
      setHistoryTemplateId(null);
      setVersions([]);
    } else {
      setHistoryTemplateId(template.id);
      try {
        const v = await getVersions(template.id);
        setVersions(v);
      } catch (error) {
        console.error("Failed to load version history:", error);
        setHistoryTemplateId(null);
        setNotification({ message: "Failed to load version history. Please try again.", kind: "error" });
      }
    }
  };

  const openNewVersionModal = async (template: PromptTemplate) => {
    setNewVersionTemplateId(template.id);
    // Pre-fill with current content
    setNewVersionContent(template.content || "");
  };

  const handleCreateVersion = async () => {
    if (typeof newVersionTemplateId !== 'string' || typeof newVersionContent !== 'string') return;
    if (!newVersionTemplateId || !newVersionContent.trim()) return;
    try {
      const result = await createVersion(newVersionTemplateId, newVersionContent);
      if (result === null) {
        setNotification({ message: "Cannot create a new version: the content is unchanged from the latest version. Please make changes before creating a new version.", kind: "info" });
        return;
      }
      // Invalidate version cache for the modified template
      versionCacheRef.current.delete(newVersionTemplateId);
      const tid = newVersionTemplateId;
      setNewVersionTemplateId(null);
      setNewVersionContent("");
      // Refresh history if open
      if (historyTemplateId === tid) {
        try {
          const v = await getVersions(tid);
          setVersions(v);
        } catch (histErr) {
          console.error("Failed to refresh version history after create:", histErr);
        }
      }
    } catch (error) {
      console.error("Failed to create version:", error);
      setNotification({ message: "Failed to create version. Please try again.", kind: "error" });
    }
  };

  const handleStatusChange = async (versionId: string, status: VersionStatus) => {
    try {
      await changeVersionStatus(versionId, status);
      setExpandedVersionId(null);
      if (historyTemplateId) {
        // Invalidate cache so next modal open re-fetches fresh data
        versionCacheRef.current.delete(historyTemplateId);
        try {
          const v = await getVersions(historyTemplateId);
          setVersions(v);
        } catch (vErr) {
          console.error("Failed to refresh versions after status change:", vErr);
        }
        await reloadTemplates();
      }
    } catch (error) {
      console.error("Failed to update version status:", error);
      setNotification({ message: "Failed to update version status. Please try again.", kind: "error" });
    }
  };

  const handleSaveDraftEdit = async () => {
    if (!editingTemplate || !draftEditContent?.trim()) return;
    setIsSavingDraft(true);
    try {
      const result = await createVersion(editingTemplate.id, draftEditContent);
      if (result === null) {
        setNotification({ message: "Content is unchanged — no new version was created.", kind: "info" });
        return;
      }
      // Invalidate cache and reload versions inside the modal
      versionCacheRef.current.delete(editingTemplate.id);
      const refreshed = await getVersions(editingTemplate.id);
      versionCacheRef.current.set(editingTemplate.id, refreshed);
      setEditVersions(refreshed);
      setSelectedEditVersionId(result.promptTemplateVersionId);
      setDraftEditContent(null);
      setNotification({ message: "Draft saved as a new version.", kind: "info" });
    } catch (error) {
      console.error("Failed to save draft:", error);
      setNotification({ message: "Failed to save draft. Please try again.", kind: "error" });
    } finally {
      setIsSavingDraft(false);
    }
  };

  // A draft is pending whenever the backend explicitly tells us so (hasPendingDraft).
  // This covers both:
  //   a) Template has ONLY a draft (no approved version yet)
  //   b) Template has an approved v1 AND a new draft v2 waiting for review
  const hasDraftPending = (template: PromptTemplate): boolean =>
    template.hasPendingDraft === true;

  const getNewVersionTemplate = (): PromptTemplate | undefined =>
    templates.find((t) => t.id === newVersionTemplateId);

  if (loading) {
    return (
      <Layout>
        <div className={styles.loading}>Loading templates...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2>Manage Prompt Templates</h2>
            <p className={styles.headerSubtitle}>Create and manage AI prompt templates for structured, governed responses</p>
          </div>
          <PrimaryButton className={styles.createPrimaryBtn} onClick={() => handleOpenModal()} text="Create New Template" iconProps={{ iconName: 'Add' }} />
        </div>

        {/* Inline notification banner — replaces browser alert() dialogs */}
        {notification && (
          <div
            className={`${styles.notificationBanner} ${notification.kind === "error" ? styles.notificationError : styles.notificationInfo}`}
            role="alert"
            aria-live="assertive"
          >
            <span>{notification.message}</span>
            <button className={styles.notificationClose} onClick={clearNotification} aria-label="Dismiss">×</button>
          </div>
        )}

        <div className={styles.templateList}>
          {templates.length === 0 ? (
            <p className={styles.emptyState}>No templates found. Create your first template!</p>
          ) : (
            templates.map((template) => (
              <div key={template.id} className={styles.templateCard}>
                <div className={styles.templateHeader}>
                  <h3>{template.name}</h3>
                  <div className={styles.actions}>
                    <button className={styles.iconButton} onClick={() => handleOpenModal(template)} title="Edit metadata">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M12.146.854a.5.5 0 0 1 .708 0l2.292 2.292a.5.5 0 0 1 0 .708l-9.146 9.146H3.5a.5.5 0 0 1-.5-.5v-2.5L12.146.854zM4 10.207V12h1.793l8.647-8.646-1.794-1.794L4 10.207z"/></svg>
                    </button>
                    <button className={styles.iconButton} onClick={() => openNewVersionModal(template)} title="Create new version">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M8 1a.5.5 0 0 1 .5.5V7h5.5a.5.5 0 0 1 0 1H8.5v5.5a.5.5 0 0 1-1 0V8H2a.5.5 0 0 1 0-1h5.5V1.5A.5.5 0 0 1 8 1z"/></svg>
                    </button>
                    <button className={`${styles.iconButton} ${historyTemplateId === template.id ? styles.iconButtonActive : ""}`} onClick={() => toggleHistory(template)} title={historyTemplateId === template.id ? "Hide history" : "Version history"}>
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M8 3.5a.5.5 0 0 1 .5.5v4l3.15 1.575a.5.5 0 1 1-.45.894l-3.35-1.675A.5.5 0 0 1 7.5 8.5V4a.5.5 0 0 1 .5-.5z"/><path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm0-1A7 7 0 1 1 8 1a7 7 0 0 1 0 14z"/></svg>
                    </button>
                    <button className={`${styles.iconButton} ${styles.iconButtonDanger}`} onClick={() => handleDelete(template.id)} title="Delete template">
                      <Delete20Regular />
                    </button>
                  </div>
                </div>

                {/* Status badges row */}
                <div className={styles.versionInfo}>
                  {/* Show Default when there is no approved current version */}
                  {(
                    // If backend reports an approved current version, show it; otherwise show Default (v0)
                    (template.currentVersion != null && template.currentStatus === 'Approved')
                      ? versionLabel(template.currentVersion)
                      : versionLabel(0)
                  ) && (
                    <span className={styles.versionBadge}>{(template.currentVersion != null && template.currentStatus === 'Approved') ? versionLabel(template.currentVersion) : versionLabel(0)}</span>
                  )}
                  <span className={styles.promptKeyBadge}>/{template.promptKey}</span>
                  {hasDraftPending(template) && (
                    <span className={`${styles.statusBadge} ${styles.statusDraft}`}>Draft pending</span>
                  )}
                </div>

                <p className={styles.description}>{template.description}</p>

                <div className={styles.metadata}>
                  <span className={styles.category}>{template.category}</span>
                  {template.tags && template.tags.length > 0 && (
                    <div className={styles.tags}>
                      {template.tags.map((tag) => (
                        <span key={`${template.id}-${tag}`} className={styles.tag}>{tag}</span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Inline Version History */}
                {historyTemplateId === template.id && (
                  <div className={styles.versionHistoryPanel}>
                    <h4>Version History &mdash; {template.promptKey}</h4>
                    <table className={styles.versionTable}>
                      <thead>
                        <tr>
                          <th>Version</th>
                          <th>Status</th>
                          <th>Updated By</th>
                          <th>Updated At</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {versions.map((v) => (
                          <React.Fragment key={v.promptTemplateVersionId}>
                            <tr
                              className={expandedVersionId === v.promptTemplateVersionId ? styles.versionRowExpanded : ''}
                            >
                              <td className={styles.versionCol}>{versionLabel(v.version)}</td>
                              <td>
                                <span className={`${styles.versionStatusBadge} ${
                                  versionStatusLabel(v) === "Approved" ? styles.statusApproved :
                                  versionStatusLabel(v) === "Draft" ? styles.statusDraft :
                                  versionStatusLabel(v) === "Default" ? styles.statusDefault :
                                  styles.statusDeprecated
                                }`}>
                                  {versionStatusLabel(v)}
                                </span>
                              </td>
                              <td className={styles.updatedByCol}>{v.createdByName || v.createdBy || "—"}</td>
                              <td className={styles.updatedAtCol}>{v.createdAt ? new Date(v.createdAt).toLocaleDateString("en-US") : "—"}</td>
                              <td className={styles.versionActions}>
                                {/* View content toggle */}
                                <button
                                  className={styles.viewContentButton}
                                  onClick={() => setExpandedVersionId(
                                    expandedVersionId === v.promptTemplateVersionId ? null : v.promptTemplateVersionId
                                  )}
                                  title={expandedVersionId === v.promptTemplateVersionId ? "Hide content" : "View content"}
                                >
                                  {expandedVersionId === v.promptTemplateVersionId ? "▲ Hide" : "▼ View"}
                                </button>
                                {v.version !== 0 && (
                                  <>
                                    {v.status === "Draft" && (
                                      <button className={styles.approveButton}
                                        onClick={() => handleStatusChange(v.promptTemplateVersionId, "Approved")}>
                                        Approve
                                      </button>
                                    )}
                                    {(v.status === "Approved") && (
                                      <button className={styles.deprecateButton}
                                        onClick={() => handleStatusChange(v.promptTemplateVersionId, "Deprecated")}>
                                        Deprecate
                                      </button>
                                    )}
                                    {v.status === "Default" && versions.some(other => other.status === "Approved") && (
                                      <button className={styles.deprecateButton}
                                        onClick={() => handleStatusChange(v.promptTemplateVersionId, "Deprecated")}>
                                        Deprecate
                                      </button>
                                    )}
                                    {v.status === "Deprecated" && (
                                      <button className={styles.reactivateButton}
                                        onClick={() => handleStatusChange(v.promptTemplateVersionId, "Reactivate")}>
                                        Reactivate
                                      </button>
                                    )}
                                  </>
                                )}
                              </td>
                            </tr>
                            {/* Inline content preview row */}
                            {expandedVersionId === v.promptTemplateVersionId && (
                              <tr className={styles.contentPreviewRow}>
                                <td colSpan={5} className={styles.contentPreviewCell}>
                                  <div className={styles.contentPreviewHeader}>
                                    <span>Content — {versionLabel(v.version)} ({v.status})</span>
                                    {v.changeDescription && (
                                      <span className={styles.changeDesc}>{v.changeDescription}</span>
                                    )}
                                    {/* Inline approve/activate removed (use main Approve button) */}
                                  </div>
                                  {/* Safe: React renders this as a text node, not innerHTML, so no XSS risk. */}
                                  <pre className={styles.contentPreview}>{v.content}</pre>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* New Version Modal — portaled to body to escape shell-content stacking context */}
        {ReactDOM.createPortal(
          (() => {
            if (!newVersionTemplateId) return null;
            const tpl = getNewVersionTemplate();
            if (!tpl) return null;
            return (
              <NewVersionModal
                tpl={tpl}
                newVersionContent={newVersionContent}
                onContentChange={setNewVersionContent}
                onClose={() => setNewVersionTemplateId(null)}
                onSubmit={handleCreateVersion}
              />
            );
          })(),
          document.body
        )}

        {/* Create/Edit Template Modal — portaled to body to escape shell-content stacking context */}
        {ReactDOM.createPortal(
          isModalOpen ? (
          <div className={styles.modalOverlay} onClick={handleCloseModal}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h2>{editingTemplate ? "Edit Template" : "Create New Template"}</h2>
                <button className={styles.closeButton} onClick={handleCloseModal}>×</button>
              </div>
              {modalError && (
                <div className={styles.modalError} role="alert">{modalError}</div>
              )}
              <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="promptKey">Prompt Key *</label>
                    <input id="promptKey" type="text" value={formData.promptKey}
                      onChange={(e) => setFormData({ ...formData, promptKey: e.target.value })}
                      required
                      placeholder="e.g., functional-spec" pattern="[a-z0-9-]+"
                      title="Only lowercase letters, numbers, and hyphens allowed" />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="name">Name *</label>
                    <input id="name" type="text" value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })} required
                      placeholder="e.g., Functional Specification" />
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="description">Description *</label>
                  <input id="description" type="text" value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required placeholder="Brief description of the template" />
                </div>
                <div className={styles.formGroup}>
                  <label>Category *</label>
                  <div className={styles.tagInputWrapper}>
                    <div
                      ref={categoryDropdownRef}
                      className={styles.tagDropdownInner}
                      onBlur={(e) => { if (!categoryDropdownRef.current?.contains(e.relatedTarget as Node)) setShowCategoryDropdown(false); }}
                    >
                      <div className={styles.tagInputBox}
                        onClick={() => {
                          const el = document.getElementById("categoryInput");
                          if (el) el.focus();
                        }}>
                        <input
                          id="categoryInput"
                          type="text"
                          value={showCategoryDropdown ? categorySearchInput : formData.category}
                          onChange={(e) => {
                            setCategorySearchInput(e.target.value);
                            if (!showCategoryDropdown) setShowCategoryDropdown(true);
                          }}
                          onFocus={() => { setShowCategoryDropdown(true); setCategorySearchInput(formData.category || ""); }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const val = categorySearchInput.trim();
                              if (val) {
                                if (!knownCategories.some(c => c.toLowerCase() === val.toLowerCase())) {
                                  setCustomCategories(prev => [...prev, val]);
                                }
                                setFormData(prev => ({ ...prev, category: val }));
                                setCategorySearchInput("");
                                setShowCategoryDropdown(false);
                              }
                            }
                          }}
                          placeholder="Search or add a category..."
                          className={styles.tagInlineInput}
                        />
                        <button
                          type="button"
                          className={styles.addTagButton}
                          aria-label="Add category"
                          disabled={!categorySearchInput.trim()}
                          onClick={() => {
                            const val = categorySearchInput.trim();
                            if (val) {
                              if (!knownCategories.some(c => c.toLowerCase() === val.toLowerCase())) {
                                setCustomCategories(prev => [...prev, val]);
                              }
                              setFormData(prev => ({ ...prev, category: val }));
                              setCategorySearchInput("");
                              setShowCategoryDropdown(false);
                            }
                          }}
                        >
                          +
                        </button>
                      </div>
                      {showCategoryDropdown && filteredCategories.length > 0 && (
                        <div className={styles.tagSuggestions}>
                          {filteredCategories.map(cat => (
                            <div
                              key={cat}
                              className={`${styles.tagSuggestionItem} ${formData.category === cat ? styles.customDropdownItemSelected : ''}`}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setFormData(prev => ({ ...prev, category: cat }));
                                setCategorySearchInput("");
                                setShowCategoryDropdown(false);
                              }}
                            >
                              {cat}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.formGroup}>
                    <label>Tags</label>
                    <div className={styles.tagInputWrapper}>
                      {formData.tags.length > 0 && (
                        <div className={styles.tagChipsRow}>
                          {formData.tags.map((tag, idx) => (
                            <span key={`formtag-${idx}-${tag}`} className={styles.tagChip}>
                              {tag}
                              <button type="button" className={styles.tagChipRemove} onClick={() => removeTag(tag)}
                                aria-label={`Remove ${tag}`}>×</button>
                            </span>
                          ))}
                        </div>
                      )}
                      {/* Wrap the input and suggestions so onBlur on the container fires
                          when focus leaves both — avoids the setTimeout race condition. */}
                      <div
                        ref={tagDropdownRef}
                        className={styles.tagDropdownInner}
                        onBlur={(e) => { if (!tagDropdownRef.current?.contains(e.relatedTarget as Node)) setShowTagSuggestions(false); }}
                      >
                      <div className={styles.tagInputBox}
                        onClick={() => {
                          const el = document.getElementById("tagInput");
                          if (el) el.focus();
                        }}>
                        <input
                          id="tagInput"
                          type="text"
                          value={tagInput}
                          onChange={(e) => { setTagInput(e.target.value); setShowTagSuggestions(true); }}
                          onFocus={() => setShowTagSuggestions(true)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ',') {
                              e.preventDefault();
                              addTag(tagInput);
                            }
                            if (e.key === 'Backspace' && !tagInput && formData.tags.length > 0) {
                              removeTag(formData.tags[formData.tags.length - 1]);
                            }
                          }}
                          placeholder="Search or add tags..."
                          className={styles.tagInlineInput}
                        />
                        <button
                          type="button"
                          className={styles.addTagButton}
                          onClick={() => addTag(tagInput)}
                          disabled={!tagInput.trim()}
                          aria-label="Add tag"
                        >
                          +
                        </button>
                      </div>
                      {showTagSuggestions && knownTags.filter(t => !formData.tags.includes(t) && t.toLowerCase().includes(tagInput.toLowerCase())).length > 0 && (
                        <div className={styles.tagSuggestions}>
                          {knownTags
                            .filter(t => !formData.tags.includes(t) && t.toLowerCase().includes(tagInput.toLowerCase()))
                            .map((tag, idx) => (
                              <div key={`suggestion-${idx}-${tag}`} className={styles.tagSuggestionItem}
                                onMouseDown={(e) => { e.preventDefault(); addTag(tag); }}>
                                {tag}
                              </div>
                          ))}
                        </div>
                      )}
                      </div>
                    </div>
                </div>
                {!editingTemplate && (
                  <div className={styles.formGroup}>
                    <label htmlFor="content">Initial Content *</label>
                    <textarea id="content" value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      required rows={12} placeholder="Template content..." />
                  </div>
                )}
                {editingTemplate && editVersions.length > 0 && (
                  <div className={styles.formGroup}>
                    <label>Version</label>
                    <div className={styles.versionSelectorRow}>
                      {/* onBlur on the container closes the dropdown race-free. */}
                      <div
                        ref={versionDropdownRef}
                        className={styles.customDropdown}
                        style={{ flex: 1 }}
                        onBlur={(e) => { if (!versionDropdownRef.current?.contains(e.relatedTarget as Node)) setShowVersionDropdown(false); }}
                      >
                        <button
                          type="button"
                          className={`${styles.customDropdownTrigger} ${showVersionDropdown ? styles.customDropdownTriggerOpen : ''}`}
                          onClick={() => setShowVersionDropdown(v => !v)}
                        >
                          <span className={`${styles.customDropdownValue} ${!selectedEditVersionId ? styles.customDropdownPlaceholder : ''}`}>
                            {selectedEditVersionId
                              ? (() => { const ver = editVersions.find(v => v.promptTemplateVersionId === selectedEditVersionId); return ver ? `${versionLabel(ver.version)} — ${versionStatusLabel(ver)}` : 'Select a version to view...'; })()
                              : 'Select a version to view...'}
                          </span>
                          <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"
                            style={{ flexShrink: 0, color: '#6b7280', transition: 'transform 0.15s', transform: showVersionDropdown ? 'rotate(180deg)' : 'none' }}>
                            <path d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
                          </svg>
                        </button>
                        {showVersionDropdown && (
                          <div className={`${styles.customDropdownMenu} ${styles.customDropdownMenuUp}`}>
                            <div
                              className={`${styles.customDropdownItem} ${styles.customDropdownPlaceholderItem} ${!selectedEditVersionId ? styles.customDropdownItemSelected : ''}`}
                              onMouseDown={(e) => { e.preventDefault(); setSelectedEditVersionId(''); setDraftEditContent(null); setShowVersionDropdown(false); }}
                            >
                              Select a version to view...
                            </div>
                            {editVersions.map(v => {
                              const isDraft = versionStatusLabel(v) === 'Draft';
                              return (
                                <div
                                  key={v.promptTemplateVersionId}
                                  className={`${styles.customDropdownItem} ${selectedEditVersionId === v.promptTemplateVersionId ? styles.customDropdownItemSelected : ''} ${!isDraft ? styles.customDropdownItemLocked : ''}`}
                                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                                  onMouseDown={(e) => { e.preventDefault(); setSelectedEditVersionId(v.promptTemplateVersionId); setDraftEditContent(isDraft ? v.content : null); setShowVersionDropdown(false); }}
                                >
                                  <span style={{ flex: 1 }}>{versionLabel(v.version)} — {versionStatusLabel(v)}</span>
                                  {!isDraft && (
                                    <span className={styles.customDropdownItemLockedBadge}>read-only</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    {(() => {
                      const sel = editVersions.find(v => v.promptTemplateVersionId === selectedEditVersionId);
                      if (!sel) return null;
                      return (
                        <div className={styles.versionPreviewBox}>
                          <div className={styles.versionPreviewMeta}>
                            <span className={`${styles.versionStatusBadge} ${
                              versionStatusLabel(sel) === "Approved" ? styles.statusApproved :
                              versionStatusLabel(sel) === "Draft" ? styles.statusDraft :
                              versionStatusLabel(sel) === "Default" ? styles.statusDefault :
                              styles.statusDeprecated
                            }`}>{versionStatusLabel(sel)}</span>
                            <span className={styles.versionPreviewInfo}>
                              by {sel.createdByName || sel.createdBy || "—"} · {sel.createdAt ? new Date(sel.createdAt).toLocaleDateString("en-US") : "—"}
                            </span>
                            {sel.version === 0 && (
                              <span className={styles.readonlyLabel}>Read-only</span>
                            )}
                          </div>
                          {/* Safe: React renders content as a text node / textarea value, not innerHTML, so no XSS risk. */}
                          {versionStatusLabel(sel) === 'Draft' ? (
                            <>
                              <textarea
                                className={styles.draftEditTextarea}
                                value={draftEditContent ?? sel.content}
                                onChange={(e) => setDraftEditContent(e.target.value)}
                                rows={14}
                                spellCheck={false}
                              />
                              <div className={styles.draftEditActions}>
                                <button
                                  type="button"
                                  className={styles.saveDraftButton}
                                  disabled={isSavingDraft || !(draftEditContent ?? '').trim() || draftEditContent === sel.content}
                                  onClick={handleSaveDraftEdit}
                                >
                                  {isSavingDraft ? "Saving…" : "Save Draft"}
                                </button>
                              </div>
                            </>
                          ) : (
                            <pre className={styles.contentPreview}>{sel.content}</pre>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
                {editingTemplate && editVersions.length === 0 && !isVersionsLoading && (
                  <div className={styles.versionNotice}>
                    <p>To change prompt content, use the <strong>New Version</strong> button on the template card.</p>
                  </div>
                )}
                {editingTemplate && isVersionsLoading && (
                  <div className={styles.versionNotice}>
                    <p>Loading version history…</p>
                  </div>
                )}
                <div className={styles.formActions}>
                  <button type="button" onClick={handleCloseModal} className={styles.cancelButton}>Cancel</button>
                  <button type="submit" className={styles.submitButton}>
                    {editingTemplate ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
          ) : null,
          document.body
        )}
      </div>

      {/* Delete confirmation dialog — portaled to body to escape shell-content stacking context */}
      {ReactDOM.createPortal(
        <ConfirmDialog
          isOpen={deleteConfirmId !== null}
          title="Delete Template"
          message="Are you sure you want to delete this template? This action cannot be undone."
          confirmLabel="Yes, Delete"
          variant="danger"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirmId(null)}
        />,
        document.body
      )}
    </Layout>
  );
};
